import * as Tooltip from "@radix-ui/react-tooltip";

import {
  CaptionButton,
  FullscreenButton,
  MuteButton,
  PIPButton,
  PlayButton,
  isTrackCaptionKind,
  useMediaPlayer,
  useMediaState,
  type MediaPlayerInstance,
} from "@vidstack/react";
import {
  Minimize as FullscreenExitIcon,
  Maximize as FullscreenIcon,
  VolumeX as MuteIcon,
  PauseIcon,
  PictureInPictureIcon as PictureInPictureExitIcon,
  PictureInPicture2 as PictureInPictureIcon,
  PlayIcon,
  StepBack,
  StepForward,
  SubtitlesIcon,
  Volume2 as VolumeHighIcon,
  Volume1 as VolumeLowIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { useDataContext } from "@/app/_context";

export interface MediaButtonProps {
  tooltipSide?: Tooltip.TooltipContentProps["side"];
  tooltipAlign?: Tooltip.TooltipContentProps["align"];
  tooltipOffset?: number;
}

export const buttonClass =
  "group ring-media-focus relative inline-flex h-10 w-10 cursor-pointer justify-center items-center rounded-md outline-none ring-inset hover:bg-white/20 focus-visible:ring-4 aria-disabled:hidden";

export const tooltipClass =
  "animate-out fade-out slide-out-to-bottom-2 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in data-[state=delayed-open]:slide-in-from-bottom-4 z-10 rounded-sm bg-black/90 px-2 py-0.5 text-sm font-medium text-white parent-data-[open]:hidden";

export const frameSkipAmountMs = 83.3;

// Moves one frame while paused. Shared by the frame-step buttons and the
// , / . keyboard shortcuts.
export function stepFrame(player: MediaPlayerInstance, direction: -1 | 1) {
  if (!player.paused) return;
  const next = player.currentTime + (direction * frameSkipAmountMs) / 1000;
  player.currentTime = Math.min(Math.max(0, next), player.duration || next);
}

export function Play({
  tooltipOffset = 0,
  tooltipSide = "top",
  tooltipAlign = "center",
}: MediaButtonProps) {
  const { isPortal } = useDataContext();
  const isPaused = useMediaState("paused");
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <PlayButton className={buttonClass}>
          {isPaused ? (
            <PlayIcon className="w-7 h-7 translate-x-px" />
          ) : (
            <PauseIcon className="w-7 h-7" />
          )}
        </PlayButton>
      </Tooltip.Trigger>
      <Tooltip.Content
        className={tooltipClass}
        side={tooltipSide}
        align={tooltipAlign}
        sideOffset={tooltipOffset}
      >
        {isPaused ? "Play" : "Pause"}
      </Tooltip.Content>
    </Tooltip.Root>
  );
}

export function Mute({
  tooltipOffset = 0,
  tooltipSide = "top",
  tooltipAlign = "center",
}: MediaButtonProps) {
  const volume = useMediaState("volume"),
    isMuted = useMediaState("muted");
  const { isPortal } = useDataContext();
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <MuteButton className={buttonClass}>
          {isMuted || volume == 0 ? (
            <MuteIcon className={cn(isPortal ? "w-4 h-4" : "w-7 h-7")} />
          ) : volume < 0.5 ? (
            <VolumeLowIcon className={cn(isPortal ? "w-4 h-4" : "w-7 h-7")} />
          ) : (
            <VolumeHighIcon className={cn(isPortal ? "w-4 h-4" : "w-7 h-7")} />
          )}
        </MuteButton>
      </Tooltip.Trigger>
      <Tooltip.Content
        className={tooltipClass}
        side={tooltipSide}
        align={tooltipAlign}
        sideOffset={tooltipOffset}
      >
        {isMuted ? "Unmute" : "Mute"}
      </Tooltip.Content>
    </Tooltip.Root>
  );
}

export function Caption({
  tooltipOffset = 0,
  tooltipSide = "top",
  tooltipAlign = "center",
}: MediaButtonProps) {
  const track = useMediaState("textTrack"),
    isOn = track && isTrackCaptionKind(track);
  const { isPortal } = useDataContext();
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <CaptionButton className={buttonClass}>
          <SubtitlesIcon
            className={`w-7 h-7 ${!isOn ? "text-white/60" : ""}`}
          />
        </CaptionButton>
      </Tooltip.Trigger>
      <Tooltip.Content
        className={tooltipClass}
        side={tooltipSide}
        align={tooltipAlign}
        sideOffset={tooltipOffset}
      >
        {isOn ? "Closed-Captions Off" : "Closed-Captions On"}
      </Tooltip.Content>
    </Tooltip.Root>
  );
}

export function PIP({
  tooltipOffset = 0,
  tooltipSide = "top",
  tooltipAlign = "center",
}: MediaButtonProps) {
  const isActive = useMediaState("pictureInPicture");
  const { isPortal } = useDataContext();
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <PIPButton className={buttonClass}>
          {isActive ? (
            <PictureInPictureExitIcon className="w-7 h-7" />
          ) : (
            <PictureInPictureIcon className="w-7 h-7" />
          )}
        </PIPButton>
      </Tooltip.Trigger>
      <Tooltip.Content
        className={tooltipClass}
        side={tooltipSide}
        align={tooltipAlign}
        sideOffset={tooltipOffset}
      >
        {isActive ? "Exit PIP" : "Enter PIP"}
      </Tooltip.Content>
    </Tooltip.Root>
  );
}

export function Fullscreen({
  tooltipOffset = 0,
  tooltipSide = "top",
  tooltipAlign = "center",
}: MediaButtonProps) {
  const isActive = useMediaState("fullscreen");
  const { isPortal } = useDataContext();
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <FullscreenButton className={buttonClass}>
          {isActive ? (
            <FullscreenExitIcon
              className={cn(isPortal ? "w-4 h-4" : "w-7 h-7")}
            />
          ) : (
            <FullscreenIcon className={cn(isPortal ? "w-4 h-4" : "w-7 h-7")} />
          )}
        </FullscreenButton>
      </Tooltip.Trigger>
      <Tooltip.Content
        className={tooltipClass}
        side={tooltipSide}
        align={tooltipAlign}
        sideOffset={tooltipOffset}
      >
        {isActive ? "Exit Fullscreen" : "Enter Fullscreen"}
      </Tooltip.Content>
    </Tooltip.Root>
  );
}

const frameSteps = [
  { direction: -1, label: "Previous frame (,)", Icon: StepBack },
  { direction: 1, label: "Next frame (.)", Icon: StepForward },
] as const;

export function FrameStep({
  tooltipOffset = 0,
  tooltipSide = "top",
  tooltipAlign = "center",
}: MediaButtonProps) {
  const player = useMediaPlayer();
  const isPaused = useMediaState("paused");
  // Paused only. They sit before the bar's spacer, so nothing else shifts.
  if (!isPaused) return null;
  return (
    <div className="ml-2 flex items-center">
      {frameSteps.map(({ direction, label, Icon }) => (
        <Tooltip.Root key={direction}>
          <Tooltip.Trigger asChild>
            <button
              type="button"
              aria-label={label}
              className={buttonClass}
              onClick={() => player && stepFrame(player, direction)}
            >
              <Icon className="w-6 h-6" />
            </button>
          </Tooltip.Trigger>
          <Tooltip.Content
            className={tooltipClass}
            side={tooltipSide}
            align={tooltipAlign}
            sideOffset={tooltipOffset}
          >
            {label}
          </Tooltip.Content>
        </Tooltip.Root>
      ))}
    </div>
  );
}
